import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CarVatType, IFilterItemViewModel } from '@ncg/data';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'ncg-product-item-price',
    template: `
        <div class="product-pricing" *ngIf="primaryPriceText || secondaryPriceText">
            <div class="product-pricing__primary" *ngIf="primaryPriceText">
                {{ primaryPriceText }}
                <div class="product-pricing__vat" *ngIf="hasPrice && vatText">{{ vatText }}</div>
            </div>
            <div class="product-pricing__secondary" *ngIf="secondaryPriceText">{{ secondaryPriceText }}</div>
        </div>
    `,
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductItemPriceComponent implements OnChanges {
    @Input()
    item: IFilterItemViewModel;
    primaryPriceText?: string | number;
    secondaryPriceText?: string | number;
    vatText?: string;
    hasPrice = false;

    @Output() priceRendered: EventEmitter<boolean> = new EventEmitter();

    constructor(
        private readonly translateService: TranslateService,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngOnChanges({ item }: SimpleChanges) {
        const { currentValue, previousValue } = item;
        const isPriceTypeEqual = currentValue?.priceType === previousValue?.priceType;
        const isCashPriceEqual = currentValue?.cashPrice === previousValue?.cashPrice;
        const isLeasingPriceEqual = currentValue?.leasingPrice === previousValue?.leasingPrice;
        if (isCashPriceEqual && isPriceTypeEqual && isLeasingPriceEqual) {
            return;
        }
        this.setPricingText(currentValue);
    }

    async setPricingText(item: IFilterItemViewModel) {
        this.primaryPriceText = undefined;
        this.secondaryPriceText = undefined;
        this.vatText = undefined;
        this.hasPrice = false;

        if (item.vatType === CarVatType.WithoutVat) {
            this.vatText = await firstValueFrom(this.translateService.get('product_item.price_ex_vat'));
        } else if (item.vatType === CarVatType.WithVat) {
            this.vatText = await firstValueFrom(this.translateService.get('product_item.price_incl_vat'));
        } else {
            this.vatText = undefined;
        }

        switch (item.priceType) {
            case 'CallForLeasing':
                this.hasPrice = Boolean(item.cashPrice);
                if (item.cashPrice) {
                    this.primaryPriceText = item.cashPrice;
                    this.secondaryPriceText = await firstValueFrom(this.translateService.get('product_item.call_for_leasing'));
                } else {
                    this.primaryPriceText = await firstValueFrom(this.translateService.get('product_item.call_for_leasing'));
                }
                break;
            case 'RetailPrice':
                this.hasPrice = Boolean(item.cashPrice);
                this.primaryPriceText = item.cashPrice;
                break;
            case 'LeasingPrice':
                this.hasPrice = Boolean(item.cashPrice || item.leasingPrice);
                if (item.cashPrice) {
                    this.primaryPriceText = item.cashPrice;
                    this.secondaryPriceText = await firstValueFrom(
                        this.translateService.get('product_item.leasing_price', { price: item.leasingPrice })
                    );
                } else {
                    this.secondaryPriceText = await firstValueFrom(
                        this.translateService.get('product_item.leasing_price', { price: item.leasingPrice })
                    );
                }
                break;
            case 'Leasing':
                this.hasPrice = Boolean(item.cashPrice || item.leasingPrice);
                if (item.cashPrice) {
                    this.primaryPriceText = item.cashPrice;
                    this.secondaryPriceText = await firstValueFrom(
                        this.translateService.get('product_item.leasing_price', { price: item.leasingPrice })
                    );
                } else {
                    this.primaryPriceText = await firstValueFrom(
                        this.translateService.get('product_item.leasing_price', { price: item.leasingPrice })
                    );
                }
                break;
            default:
                this.primaryPriceText = await firstValueFrom(this.translateService.get('product_item.call_for_price'));
                break;
        }

        this.priceRendered.emit();

        this.cd.markForCheck();
    }
}
